// The relay WebSocket: connect, answer the challenge with the device key, keep alive, reconnect
// with jittered backoff, and wake up when the page becomes visible or the network returns. Chrome kills the socket
// when it freezes the page or puts it in the back-forward cache, often without a close event, so a freeze drops the
// socket and a resume or a restore from that cache connects afresh (Page Lifecycle).
// Routing only (docs/protocol.md, "Relay authentication"); envelopes are handled in transport.ts.
import { relayAuthText, signText, type ClientFrame, type RelayFrame } from "@whistle/protocol";

export type RelayStatus = "connecting" | "connected" | "reconnecting" | "closed";

/** The relay closes with this code when authentication fails: the device key is not registered (or was removed). */
export const CLOSE_AUTH_FAILED = 4001;
/** The relay closes an authenticated socket with this code when the machine removes the device. */
export const CLOSE_REMOVED = 4003;

export type RelayClientOptions = {
  relayOrigin: string;
  accountId: string;
  deviceId: string;
  signKey: CryptoKey;
  onFrame: (frame: RelayFrame) => void;
  onStatus: (status: RelayStatus, detail?: { code?: number; attempt?: number }) => void;
  /**
   * Called on close code 4001, with how many closes in a row have been 4001 with no successful auth between. Return
   * "retry" to keep trying (pairing, when the machine hasn't added us yet) or "stop".
   */
  onAuthRejected?: (inARow: number) => "retry" | "stop";
  /** Called on close code 4003, after which the client stops. Without it, 4003 is an ordinary close. */
  onRemoved?: () => void;
  WebSocketImpl?: typeof WebSocket;
  backoffBaseMs?: number;
  backoffMaxMs?: number;
  pingEveryMs?: number;
  pongTimeoutMs?: number;
  /** How long to wait for the pong asked for when the page becomes visible again. */
  wakePongTimeoutMs?: number;
  doc?: Document;
  win?: Window;
  random?: () => number;
};

export function wsUrl(relayOrigin: string, accountId: string): string {
  const origin = relayOrigin.replace(/\/+$/, "").replace(/^http/, "ws");
  return `${origin}/v1/connect?account=${encodeURIComponent(accountId)}`;
}

/** Full jitter backoff: 0..min(max, base*2^attempt). */
export function backoffDelay(attempt: number, baseMs: number, maxMs: number, random = Math.random): number {
  const cap = Math.min(maxMs, baseMs * 2 ** Math.min(attempt, 10));
  return Math.floor(random() * cap) + Math.min(250, cap);
}

export class RelayClient {
  private ws: WebSocket | null = null;
  private stopped = true;
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private pingTimer: ReturnType<typeof setInterval> | undefined;
  private pongTimer: ReturnType<typeof setTimeout> | undefined;
  private authed = false;
  private authRejections = 0;
  private connectStartedAt = 0;
  private readonly opts: RelayClientOptions;

  constructor(opts: RelayClientOptions) {
    this.opts = opts;
  }

  get connected(): boolean {
    return this.authed && this.ws?.readyState === WebSocket.OPEN;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.attempt = 0;
    this.authRejections = 0;
    const doc = this.opts.doc ?? (typeof document !== "undefined" ? document : undefined);
    const win = this.opts.win ?? (typeof window !== "undefined" ? window : undefined);
    doc?.addEventListener("visibilitychange", this.onVisible);
    doc?.addEventListener("freeze", this.onFreeze);
    doc?.addEventListener("resume", this.onResume);
    win?.addEventListener("online", this.onOnline);
    win?.addEventListener("pageshow", this.onPageShow);
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    const doc = this.opts.doc ?? (typeof document !== "undefined" ? document : undefined);
    const win = this.opts.win ?? (typeof window !== "undefined" ? window : undefined);
    doc?.removeEventListener("visibilitychange", this.onVisible);
    doc?.removeEventListener("freeze", this.onFreeze);
    doc?.removeEventListener("resume", this.onResume);
    win?.removeEventListener("online", this.onOnline);
    win?.removeEventListener("pageshow", this.onPageShow);
    clearTimeout(this.reconnectTimer);
    this.dropSocket("bye");
    this.opts.onStatus("closed");
  }

  /** Reconnect now (page shown, network back). No-op when already open. */
  wake(): void {
    if (this.stopped) return;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;
    clearTimeout(this.reconnectTimer);
    this.attempt = 0;
    this.connect();
  }

  /**
   * Drop the socket, whatever state it reports, and connect again now. For a socket the browser may have killed
   * silently: after a freeze, a back-forward cache restore, or a ping that went unanswered. A socket this client
   * opened moments ago is left to finish, so a resume followed by a pageshow connects once.
   */
  restart(): void {
    if (this.stopped) return;
    if (this.ws?.readyState === WebSocket.CONNECTING && Date.now() - this.connectStartedAt < 1500) return;
    clearTimeout(this.reconnectTimer);
    this.dropSocket("restart");
    this.attempt = 0;
    this.connect("reconnecting");
  }

  send(frame: ClientFrame): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    if (frame.t !== "auth" && !this.authed) return false;
    this.ws.send(JSON.stringify(frame));
    return true;
  }

  private onVisible = () => {
    const doc = this.opts.doc ?? document;
    if (doc.hidden) return;
    this.wake();
    // An open socket may still be dead after the phone slept; a quick ping finds out in seconds, not at the next keepalive.
    if (this.authed && this.ws?.readyState === WebSocket.OPEN) this.ping(this.opts.wakePongTimeoutMs ?? 5000);
  };
  private onOnline = () => this.wake();
  private onPageShow = (ev: Event) => {
    if ((ev as PageTransitionEvent).persisted) this.restart();
  };
  private onResume = () => this.restart();
  /** The page is about to be frozen: nothing can be received until it resumes, so stop pretending to be connected. */
  private onFreeze = () => {
    if (this.stopped) return;
    clearTimeout(this.reconnectTimer);
    this.dropSocket("frozen");
    this.opts.onStatus("reconnecting");
  };

  /** Forget the current socket (its late events are ignored) and close it if it still might be open. */
  private dropSocket(reason: string): void {
    const ws = this.ws;
    this.ws = null;
    this.authed = false;
    this.clearKeepalive();
    if (!ws) return;
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      try {
        ws.close(1000, reason);
      } catch {
        /* already gone */
      }
    }
  }

  private connect(status?: "connecting" | "reconnecting"): void {
    if (this.stopped) return;
    const Impl = this.opts.WebSocketImpl ?? WebSocket;
    this.opts.onStatus(status ?? (this.attempt === 0 ? "connecting" : "reconnecting"), { attempt: this.attempt });
    this.connectStartedAt = Date.now();
    let ws: WebSocket;
    try {
      ws = new Impl(wsUrl(this.opts.relayOrigin, this.opts.accountId));
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    this.authed = false;
    ws.onmessage = (ev) => void this.onMessage(ws, typeof ev.data === "string" ? ev.data : "");
    ws.onclose = (ev) => this.onClose(ws, ev.code);
    ws.onerror = () => {
      /* onclose follows */
    };
  }

  private async onMessage(ws: WebSocket, data: string): Promise<void> {
    if (ws !== this.ws) return;
    let frame: RelayFrame;
    try {
      frame = JSON.parse(data) as RelayFrame;
    } catch {
      return;
    }
    if (!frame || typeof frame !== "object" || typeof (frame as { t?: unknown }).t !== "string") return;
    if (frame.t === "challenge") {
      const sig = await signText(this.opts.signKey, relayAuthText(this.opts.accountId, this.opts.deviceId, frame.nonce));
      if (ws !== this.ws || ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({ t: "auth", kind: "device", id: this.opts.deviceId, sig } satisfies ClientFrame));
      return;
    }
    if (frame.t === "ready") {
      this.authed = true;
      this.attempt = 0;
      this.authRejections = 0;
      this.startKeepalive();
      this.opts.onStatus("connected");
    }
    if (frame.t === "pong") {
      clearTimeout(this.pongTimer);
      return;
    }
    clearTimeout(this.pongTimer);
    this.opts.onFrame(frame);
  }

  private onClose(ws: WebSocket, code: number): void {
    if (ws !== this.ws) return;
    this.ws = null;
    const wasAuthed = this.authed;
    this.authed = false;
    this.clearKeepalive();
    if (this.stopped) return;
    if (code !== CLOSE_AUTH_FAILED) this.authRejections = 0;
    if (code === CLOSE_REMOVED && this.opts.onRemoved) {
      this.stopped = true;
      this.opts.onRemoved();
      this.opts.onStatus("closed", { code });
      return;
    }
    if (code === CLOSE_AUTH_FAILED) {
      const decision = this.opts.onAuthRejected?.(++this.authRejections) ?? "stop";
      if (decision === "stop") {
        this.stopped = true;
        this.opts.onStatus("closed", { code });
        return;
      }
    }
    this.opts.onStatus("reconnecting", { code });
    if (wasAuthed) this.attempt = 0;
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    clearTimeout(this.reconnectTimer);
    const delay = backoffDelay(this.attempt, this.opts.backoffBaseMs ?? 1000, this.opts.backoffMaxMs ?? 30_000, this.opts.random);
    this.attempt++;
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  private startKeepalive(): void {
    this.clearKeepalive();
    const every = this.opts.pingEveryMs ?? 25_000;
    this.pingTimer = setInterval(() => this.ping(this.opts.pongTimeoutMs ?? 10_000), every);
  }

  private ping(timeoutMs: number): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({ t: "ping" } satisfies ClientFrame));
    clearTimeout(this.pongTimer);
    // Nothing came back: the socket is dead even if the browser hasn't noticed, and its close event may never come.
    this.pongTimer = setTimeout(() => this.restart(), timeoutMs);
  }

  private clearKeepalive(): void {
    clearInterval(this.pingTimer);
    clearTimeout(this.pongTimer);
  }
}
